import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import {
  MeetingDetailsDto,
   CategoryDto,
   Language,
   AvailabilitySlotDetailsDto,
   TeamsWithSupportersDto,
   SupporterFilters,
   SupporterEditDto,
   CategoryBase} from '../../shared/model';

import { SchedulerViewService } from './scheduler-view.service';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { SharedService } from '../../shared/shared.service';
import { TranslatedNotificationService } from '../../shared/translation/translated-notification.service';

import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-scheduler-view',
  templateUrl: './scheduler-view.component.html',
  styleUrls: ['./scheduler-view.component.css',
  '../../shared/styles/small-navigation.css',
  '../../shared/styles/filters.css']
})
export class SchedulerViewComponent implements OnInit {

  filtersCategory: Array<CategoryDto>;
  filtersLanguage: Array<Language>;
  availabilitySlots: Array<AvailabilitySlotDetailsDto>;
  filteredAvailabilitySlots: Array<AvailabilitySlotDetailsDto> = [];
  upcomingMeetings: Array<MeetingDetailsDto>;
  filteredMeetings: Array<MeetingDetailsDto>;
  allCategories: Array<CategoryDto>;
  allLanguages: Array<Language>;
  allTeams: Array<TeamsWithSupportersDto>;
  allSupporters: Array<SupporterEditDto>;
  filters: SupporterFilters = <SupporterFilters>{
    SelectedCategory: [],
    SelectedLanguages: [],
    SelectedSupporters: [],
    ShowAvailability: false
  };
  showAvailability = false;
  meetingToShow: MeetingDetailsDto = <MeetingDetailsDto>{};
  translatedCategories: Array<CategoryBase>;

  constructor(private schedulerViewService: SchedulerViewService,
    private spinner: SpinnerService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private notificationService: TranslatedNotificationService,
    private sharedService: SharedService) { }

  ngOnInit() {
    this.spinner.show('spinner');

    return forkJoin(
      [this.schedulerViewService.getSupportersMeetings(),
      this.schedulerViewService.getSupportersByTeam(),
      this.sharedService.getSupporters(),
      this.schedulerViewService.getCategories(),
      this.schedulerViewService.getAvailabilitySlotsDetails()])
        .subscribe(([meetings, teamsWithMembers, supporters, categories, availabilities]) => {
          this.upcomingMeetings = this.translateCategoryNameInMeetings(meetings);
          this.allTeams = teamsWithMembers;
          this.allCategories = this.sharedService.translateCategories(categories);
          this.allSupporters = supporters.filter(s => s.IsRemoved === false);
          this.allLanguages = this.sharedService.getTranslatedLanguages();
          this.availabilitySlots = availabilities;
          this.filteredMeetings = this.upcomingMeetings;

          this.filterSlots();
          this.checkUrlParameters();

          this.spinner.hide('spinner');
        },
        (err) => {
          this.spinner.hide('spinner');
          this.notificationService.showMsgError('error.loading_data', 'Error while loading data');
        });
  }

  private filterSlots() {
    if (this.filters.SelectedLanguages.length > 0
      && this.filters.SelectedCategory.length > 0
      && this.filters.SelectedSupporters.length > 0) {

      this.filteredMeetings = this.upcomingMeetings.filter(x => this.containsSupporter(x));
      this.filteredAvailabilitySlots = this.availabilitySlots.filter(x => this.containsSupporterSlot(x));

      this.filteredMeetings = this.filteredMeetings.filter(x => this.containsLanguage(x));
      this.filteredAvailabilitySlots = this.filteredAvailabilitySlots.filter(x => this.containsSupporterLanguage(x));

      this.filteredMeetings =  this.filteredMeetings.filter(x => this.containsCategory(x));
      this.filteredAvailabilitySlots = this.filteredAvailabilitySlots.filter(x => this.containsSupporterCategory(x));

    } else {
      this.filteredMeetings = [];
      this.filteredAvailabilitySlots = [];
    }

    this.showAvailability = this.filters.ShowAvailability;
  }

  onFiltersChange(event) {
    if (event) {
      this.filters = event;
      this.filterSlots();
    }
  }

  refreshFiltersAfterCancel(meetingId) {
    this.removeMeetingFromUpcomingMeetings(meetingId);
    this.filterSlots();
  }

  refreshFiltersAfterChange() {
    this.filterSlots();
  }

  private containsSupporter(element: MeetingDetailsDto) {
    return this.filters.SelectedSupporters.indexOf(element.SupporterId) > -1;
  }

  private containsLanguage(element: MeetingDetailsDto) {
    return this.filters.SelectedLanguages.indexOf(element.LanguageId) > -1;
  }

  private containsCategory(element: MeetingDetailsDto) {
    return this.filters.SelectedCategory.indexOf(element.CategoryId) > -1;
  }

  private containsSupporterCategory(categories: AvailabilitySlotDetailsDto) {
    const collection = this.filters.SelectedCategory.filter(x => categories.CategoryIdCollection.indexOf(x) > -1);
    return collection.length > 0;
  }

  private containsSupporterLanguage(languages: AvailabilitySlotDetailsDto) {
    const collection = this.filters.SelectedLanguages.filter(x => languages.LanguageIdCollection.indexOf(x) > -1);
    return collection.length > 0;
  }

  private containsSupporterSlot(element: AvailabilitySlotDetailsDto): boolean {
    return this.filters.SelectedSupporters.indexOf(element.SupporterId) > -1;
  }

  private removeMeetingFromUpcomingMeetings(id: number) {
    const index = this.upcomingMeetings.findIndex(m => m.Id === id);

    if (index > -1) {
      this.upcomingMeetings.splice(index, 1);
    }
  }

  private checkUrlParameters() {
    this.activatedRoute.queryParams
    .subscribe( val => {
      if (!!val['start'] && !!val['suppId']) {
        this.openMeetingForAvailabilitySlot(val['start'], val['suppId']);
      }
    });
  }

  private openMeetingForAvailabilitySlot(start: Date, supporterId: string) {
    this.meetingToShow = this.upcomingMeetings.find(m => m.Start === start && m.SupporterId === supporterId);

    if (this.meetingToShow === undefined) {
      this.notificationService.showMsgError('error.meeting_has_changed', 'This meeting has been changed and details cannot be displayed.');
    }

    this.location.replaceState(this.location.path().split('?')[0], '');
  }

  private translateCategoryNameInMeetings(meetings: MeetingDetailsDto[]): MeetingDetailsDto[] {
    const translations = this.sharedService.getTranslatedCategories();

    return meetings.map(meeting => {
      const translation = translations.find(t => t.Id === meeting.CategoryId);
      meeting.CategoryName = this.getTranslatedCategoryName(meeting, translation);
      return meeting;
    });
  }

  private getTranslatedCategoryName(meeting: MeetingDetailsDto, translation: CategoryBase): string {
    if (!!translation && !!translation.Name && translation.Name.trim() !== '') {
      return translation.Name;
    }

    return meeting.CategoryName;
  }
}
